import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Alert,
  Button,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { 
  UserX, 
  UserPlus,
  Pencil, 
  Crown, 
  Copy, 
  Check,
  Link as LinkIcon,
  Users,
  Truck,
  Send,
} from 'lucide-react';
import { MemberEditModal } from './modals/MemberEditModal';
import { MemberAddModal } from './modals/MemberAddModal';
import { RemoveMemberDialog } from './modals/RemoveMemberDialog';

const MEMBER_TYPE_LABELS = {
  courier: 'Kuriiri',
  sender: 'Lähettäjä',
  any: 'Molemmat',
};

const MEMBER_TYPE_COLORS = {
  courier: 'info',
  sender: 'warning',
  any: 'default',
};

// Filter options — 'all' means no filtering applied
const MEMBER_FILTERS = [
  { value: 'all',     label: 'Kaikki',    icon: Users },
  { value: 'courier', label: 'Kuriirit',  icon: Truck },
  { value: 'sender',  label: 'Lähettäjät', icon: Send },
  { value: 'any',     label: 'Molemmat',  icon: Users },
];

export const OrganizationMembers = ({
  user,
  organizationData,
  organizationId,
  isOwner,
  getMembers,
  addMember,    // (memberData) => Promise
  removeMember,
  updateMember, // (userId, updatedFields) => Promise
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all'); // default: no filtering

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, member: null });
  const [removeDialog, setRemoveDialog] = useState({ open: false, member: null });

  const flash = (setter, value, ms = 3000) => {
    setter(value);
    setTimeout(() => setter(null), ms);
  };

  const handleCopyInviteLink = () => {
    const link = `${window.location.origin}/join?organization=${organizationId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const handleCopyUid = (uid) => {
    navigator.clipboard.writeText(uid);
    flash(setSuccess, 'Käyttäjätunnus kopioitu');
  };

  const handleEditSave = async (userId, updatedFields) => {
    setLoading(true);
    setError(null);
    try {
      await updateMember(userId, updatedFields);
      setEditDialog({ open: false, member: null });
      flash(setSuccess, 'Jäsenen tiedot päivitetty');
    } catch (err) {
      setError(err.message || 'Jäsenen päivitys epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCreate = async (memberData) => {
    setLoading(true);
    setError(null);
    try {
      await addMember(memberData);
      setAddDialogOpen(false);
      flash(setSuccess, 'Jäsen lisätty');
    } catch (err) {
      setError(err.message || 'Jäsenen lisääminen epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveConfirm = async () => {
    const member = removeDialog.member;
    setRemoveDialog({ open: false, member: null });
    setLoading(true);
    setError(null);
    try {
      await removeMember(member.userId);
      flash(setSuccess, 'Jäsen poistettu');
    } catch (err) {
      setError(err.message || 'Jäsenen poistaminen epäonnistui');
    } finally {
      setLoading(false);
    }
  };

  const members = getMembers();

  // 'all' => no filtering; otherwise match memberType exactly
  const filteredMembers = useMemo(
    () => (typeFilter === 'all' ? members : members.filter((m) => m.memberType === typeFilter)),
    [members, typeFilter]
  );

  const handleFilterChange = (_e, value) => {
    // ToggleButtonGroup (exclusive) fires null when clicking the active button again —
    // fall back to 'all' instead of leaving the filter unset
    setTypeFilter(value ?? 'all');
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>
      )}

      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 2,
      }}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Jäsenet</Typography>
          <Typography variant="body2" color="text.secondary">
            {typeFilter === 'all'
              ? `${members.length} jäsentä organisaatiossa`
              : `${filteredMembers.length} / ${members.length} jäsentä`}
          </Typography>
        </Box>
        {isOwner && (
          <Button
            variant="contained"
            size="small"
            startIcon={<UserPlus size={15} />}
            onClick={() => setAddDialogOpen(true)}
            disabled={loading}
          >
            Lisää jäsen
          </Button>
        )}
      </Box>

      {/* Member type filter */}
      <ToggleButtonGroup
        value={typeFilter}
        exclusive
        onChange={handleFilterChange}
        size="small"
        sx={{ mb: 2, flexWrap: 'wrap' }}
      >
        {MEMBER_FILTERS.map(({ value, label, icon: Icon }) => (
          <ToggleButton key={value} value={value} sx={{ px: 1.5, py: 0.5, gap: 0.5, textTransform: 'none' }}>
            <Icon size={14} />
            <Typography variant="caption" fontWeight={600}>{label}</Typography>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: 'action.hover' } }}>
              <TableCell>Nimi</TableCell>
              {!isMobile && <TableCell>Yhteystiedot</TableCell>}
              <TableCell>Tyyppi</TableCell>
              {isOwner && <TableCell align="right">Toiminnot</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMembers.length === 0 && (
              <TableRow>
                <TableCell colSpan={isMobile ? (isOwner ? 3 : 2) : (isOwner ? 4 : 3)} align="center" sx={{ py: 3, color: 'text.disabled' }}>
                  Ei jäseniä valitulla suodattimella
                </TableCell>
              </TableRow>
            )}
            {filteredMembers.map((member) => {
              const isCurrentUser = member.userId === user?.uid;
              const isOrgOwner = member.userId === organizationData?.ownerId;

              return (
                <TableRow
                  key={member.userId}
                  hover
                  sx={{ '&:last-child td': { border: 0 } }}
                >
                  {/* Name */}
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" fontWeight={500}>
                        {member.name || '—'}
                      </Typography>
                      {isOrgOwner && (
                        <Tooltip title="Omistaja">
                          <Crown size={14} color={theme.palette.primary.main} />
                        </Tooltip>
                      )}
                      {isCurrentUser && (
                        <Chip label="Sinä" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                    </Box>
                    {/* UID row */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
                      <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace', fontSize: '0.65rem' }}>
                        {member.userId.slice(0, 12)}…
                      </Typography>
                      <Tooltip title="Kopioi UID">
                        <IconButton size="small" sx={{ p: 0.25 }} onClick={() => handleCopyUid(member.userId)}>
                          <Copy size={10} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>

                  {/* Contact */}
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {member.email || '—'}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {member.phone || ''}
                      </Typography>
                    </TableCell>
                  )}

                  {/* Member type */}
                  <TableCell>
                    {member.memberType ? (
                      <Chip
                        label={MEMBER_TYPE_LABELS[member.memberType] || member.memberType}
                        size="small"
                        color={MEMBER_TYPE_COLORS[member.memberType] || 'default'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    ) : '—'}
                  </TableCell>

                  {/* Actions */}
                  {isOwner && (
                    <TableCell align="right">
                      <Tooltip title="Muokkaa">
                        <IconButton
                          size="small"
                          onClick={() => setEditDialog({ open: true, member })}
                          disabled={loading}
                        >
                          <Pencil size={15} />
                        </IconButton>
                      </Tooltip>
                      {!isOrgOwner && (
                        <Tooltip title="Poista">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setRemoveDialog({ open: true, member })}
                            disabled={loading}
                          >
                            <UserX size={15} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Invite link — owner only */}
      {isOwner && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Kutsu jäseniä</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Jaa tämä linkki kutsuaksesi uusia jäseniä
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={`${window.location.origin}/join?organization=${organizationId}`}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <InputAdornment position="start">
                  <LinkIcon size={16} style={{ opacity: 0.5 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copied ? 'Kopioitu!' : 'Kopioi linkki'}>
                    <IconButton size="small" onClick={handleCopyInviteLink} color={copied ? 'success' : 'default'}>
                      {copied ? <Check size={16} /> : <Copy size={16} />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Dialogs */}
      <MemberAddModal
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onCreate={handleAddCreate}
        loading={loading}
      />

      <MemberEditModal
        open={editDialog.open}
        member={editDialog.member}
        onClose={() => setEditDialog({ open: false, member: null })}
        onSave={handleEditSave}
        loading={loading}
      />

      <RemoveMemberDialog
        open={removeDialog.open}
        member={removeDialog.member}
        onClose={() => setRemoveDialog({ open: false, member: null })}
        onConfirm={handleRemoveConfirm}
        loading={loading}
      />
    </Box>
  );
};